import React from 'react';
import { MessageSquare, ArrowLeft, Calendar, Clock, User, Bot } from 'lucide-react';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ContactInfoBadge } from './ContactInfoBadge';
import { highlightContactInfo, hasContactInfo } from '../../utils/contactInfoHighlight';
import type { Conversation } from '../../types/conversations';
import type { ContactInfo } from '../../utils/contactInfoDetection';

interface ConversationWithContactInfo extends Conversation {
  contact_info?: ContactInfo[];
}

interface ConversationDetailsProps {
  conversation: ConversationWithContactInfo | null;
  onBack?: () => void;
  showBackButton?: boolean;
}

export function ConversationDetails({ conversation, onBack, showBackButton = false }: ConversationDetailsProps) {
  if (!conversation) {
    return (
      <div className="bg-gradient-to-br from-gray-800/50 to-black/50 backdrop-blur-xl rounded-xl p-8 border border-white/10 text-center h-[calc(100vh-13rem)] md:h-[600px] flex items-center justify-center animate-fade-in">
        <div className="max-w-md bg-white/5 p-6 rounded-xl border border-white/10 backdrop-blur-md shadow-2xl transform transition-all">
          <MessageSquare className="w-12 h-12 text-indigo-400 mx-auto mb-4 opacity-80" />
          <h3 className="text-white text-lg font-medium mb-2">No conversation selected</h3>
          <p className="text-gray-400 text-sm md:text-base">Select a conversation from the list to see its details</p>
        </div>
      </div>
    );
  }

  // Formatter la date en anglais
  const formattedDate = format(new Date(conversation.created_at), 'PPP', { locale: enUS });

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-black/50 backdrop-blur-xl rounded-xl border border-white/10 h-[calc(100vh-13rem)] md:h-[600px] flex flex-col overflow-hidden shadow-lg animate-fade-in">
      <div className="p-4 md:p-5 border-b border-white/10 bg-black/20">
        <div className="flex items-center gap-2 mb-2">
          {showBackButton && onBack && (
            <button 
              onClick={onBack}
              className="p-1.5 rounded-full bg-gradient-to-r from-violet-600/20 to-cyan-500/20 text-white transition-all md:hidden"
              aria-label="Back to conversations list"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-base md:text-lg font-semibold text-white">
              <Calendar className="w-4 h-4 text-white" />
              <span>{formattedDate}</span>
            </div>
            <span className="text-gray-400 mx-1.5">—</span>
            <div className="text-base md:text-lg text-white font-medium line-clamp-1">
              {conversation.initial_message_content}
            </div>
          </div>
        </div>
        
        {conversation.contact_info && conversation.contact_info.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            <p className="text-xs text-gray-500 mr-2 flex items-center">
              <span className="mr-1">Contact info:</span>
            </p>
            {conversation.contact_info.map((info: ContactInfo, index: number) => (
              <ContactInfoBadge key={index} info={info} />
            ))}
          </div>
        )}
      </div>
      
      <div className="flex-1 overflow-y-auto p-3 md:p-6 bg-gradient-to-b from-black/0 to-indigo-950/10">
        <div className="space-y-4 md:space-y-6 max-w-4xl mx-auto">
          {conversation.messages?.map((message, index) => {
            const hasContact = hasContactInfo(message.content);
            const isBot = message.role === 'bot';
            // Délai d'animation progressif
            const animationDelay = `${index * 75}ms`;
            
            return (
              <div
                key={message.id}
                style={{ animationDelay }}
                className={`flex ${isBot ? 'justify-start' : 'justify-end'} animate-fade-in-up`}
              >
                {/* Avatar pour le bot ou l'utilisateur */}
                <div className={`flex items-start gap-3 max-w-[90%] md:max-w-[80%]`}>
                  {isBot && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-500/30 to-cyan-500/30 flex items-center justify-center flex-shrink-0 mt-1">
                      <Bot className="w-4 h-4 text-violet-300" />
                    </div>
                  )}
                  
                  <div
                    className={`rounded-lg px-4 py-3 shadow-md ${
                      isBot
                        ? 'bg-white/5 text-gray-200 rounded-tl-none'
                        : 'bg-gradient-to-r from-violet-600/80 to-cyan-500/80 text-white rounded-tr-none'
                    }`}
                  >
                    <div 
                      className="whitespace-pre-wrap text-sm md:text-base"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightContactInfo(message.content) 
                      }}
                    />
                    <div className="flex justify-between items-center mt-2 pt-1 border-t border-white/10">
                      <span className="text-xs opacity-75 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(new Date(message.created_at), 'HH:mm', { locale: enUS })}
                      </span>
                      <span className="text-xs opacity-75">{isBot ? 'Chatbot' : 'User'}</span>
                    </div>
                  </div>
                  
                  {!isBot && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-500/30 to-cyan-500/30 flex items-center justify-center flex-shrink-0 mt-1">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}